import React, { useEffect, useState } from "react";
import {
  Container,
  Form,
  Button,
  Card,
  Row,
  Col,
  Badge,
  ListGroup,
  ProgressBar,
} from "react-bootstrap";
import { FaChartLine, FaBullhorn, FaDollarSign } from "react-icons/fa";
import { isInIframe } from "../../utils/is-in-iframe";
import { ToolsHeader } from "../components/ToolsHeader";

const industryBenchmarks: Record<string, number> = {
  "Real Estate": 2.5,
  Technology: 3.0,
  "E-commerce": 1.75,
  Healthcare: 2.2,
  Education: 1.9,
  Automotive: 2.4,
};

const recommendations = [
  "Gradually expand your ad budget while maintaining targeting quality",
  "Test new ad formats like video ads or carousel ads to scale reach",
  "Experiment with broader audience targeting and new geographic regions",
  "Consider bidding on more competitive, high-value keywords",
  "Implement automated bidding strategies to maintain efficiency",
  "Refine negative keyword lists to reduce wasted ad spend",
  "Improve ad copy and creative quality to boost CTR",
  "Optimize landing page speed and relevance to improve Quality Score",
  "Use remarketing campaigns to re-engage high-intent audiences",
  "Schedule ads for peak performance times to maximize ROI",
  "Adjust bids by demographics and devices for better targeting",
  "Leverage lookalike and custom audiences for smarter reach",
  "Monitor competitor keywords and ads to stay competitive",
];

const getPerformanceLabel = (cpc: number, benchmark: number) => {
  if (cpc < benchmark * 0.5)
    return (
      <span
        style={{
          backgroundColor: "#d1fadc",
          color: "#0f5132",
          borderRadius: "20px",
          padding: "4px 12px",
          fontWeight: 500,
          maxWidth: "16rem",
          textAlign: "center",
        }}
      >
        Excellent CPC Performance
      </span>
    );
  if (cpc < benchmark)
    return (
      <span
        style={{
          backgroundColor: "#fff3cd",
          color: "#664d03",
          borderRadius: "20px",
          padding: "4px 12px",
          fontWeight: 500,
          maxWidth: "16rem",
          textAlign: "center",
        }}
      >
        Good CPC Performance
      </span>
    );
  return (
    <span
      style={{
        backgroundColor: "#f8d7da",
        color: "#842029",
        borderRadius: "20px",
        padding: "4px 12px",
        fontWeight: 500,
        maxWidth: "16rem",
        textAlign: "center",
      }}
    >
      High CPC - Needs Optimization
    </span>
  );
};

const CpcCalculator: React.FC = () => {
  const [adSpend, setAdSpend] = useState("");
  const [clicks, setClicks] = useState("");
  const [industry, setIndustry] = useState("Real Estate");
  const [cpc, setCpc] = useState<number | null>(null);
  const [recoData, setRecoData] = useState<string[]>([]);

  useEffect(() => {
    // if (!cpc) {
    //   return;
    // }
    const height = document.body.scrollHeight;
    window.parent.postMessage({ type: "setHeight", height }, "*");
  }, [cpc]);
  const calculateCPC = (e: any) => {
    e.preventDefault();
    const spend = parseFloat(adSpend);
    const totalClicks = parseFloat(clicks);
    if (!isNaN(spend) && !isNaN(totalClicks) && totalClicks !== 0) {
      const height = document.body.scrollHeight;
      window.parent.postMessage({ type: "setHeight", height }, "*");
      setCpc(parseFloat((spend / totalClicks).toFixed(2)));
      setRecoData(getRandomRecommendations(recommendations));
    } else {
      setCpc(null);
    }
  };

  const benchmark = industryBenchmarks[industry];
  const getRandomRecommendations = (arr: string[], count = 5) => {
    const shuffled = [...arr].sort(() => 0.5 - Math.random());
    return shuffled.slice(0, count);
  };
  return (
    <Container className="py-0">
      <Card className="shadow-sm p-4 mb-0 border-0">
        {!isInIframe && (
          <ToolsHeader
            title="CPC Calculator"
            description="Calculate Cost Per Click (CPC) accurately to optimize and
                improve your ad campaigns."
          />
        )}
        {/* <Card.Body>
          <FaChartLine size={40} color="#4F46E5" className="mb-3" />
          <h3 className="fw-bold">CPC Calculator</h3>
          <p className="text-muted">Evaluate your advertising cost efficiency</p>
        </Card.Body> */}

        <Form>
          <Form.Group className="mb-3">
            <Form.Label>Total Ad Spend ($)</Form.Label>
            <Form.Control
              type="number"
              value={adSpend}
              onChange={(e) => setAdSpend(e.target.value)}
              placeholder="Enter total ad spend"
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Total Clicks</Form.Label>
            <Form.Control
              type="number"
              value={clicks}
              onChange={(e) => setClicks(e.target.value)}
              placeholder="Enter total clicks"
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Industry</Form.Label>
            <Form.Select
              value={industry}
              onChange={(e) => setIndustry(e.target.value)}
            >
              {Object.keys(industryBenchmarks).map((ind) => (
                <option key={ind}>{ind}</option>
              ))}
            </Form.Select>
          </Form.Group>
          <div className="d-grid mt-3">
            <Button
              type="submit"
              variant="primary"
              size="lg"
              onClick={calculateCPC}
              style={{ backgroundColor: "#8cc540" }}
            >
              Calculate CPC
            </Button>
          </div>
        </Form>
      </Card>

      {cpc !== null && (
        <Card className="shadow-sm p-4 border-0 mt-2">
          <h5 className="fw-bold mb-3">Analysis Results</h5>
          <Row className="align-items-center mb-3">
            <Col md={6}>
              <h1 className="text-dark fw-bold mb-0">${cpc.toFixed(2)}</h1>
              <div className="text-muted">per click</div>
            </Col>
            <Col md={6} className="text-md-end text-start mt-3 mt-md-0">
              {getPerformanceLabel(cpc, benchmark)}
            </Col>
          </Row>

          <div
            className="p-3 mb-4"
            style={{
              backgroundColor: "#f8f9fa",
              borderRadius: "12px",
              border: "1px solid #e0e0e0",
            }}
          >
            <div className="d-flex justify-content-between align-items-center mb-2">
              <span className="text-muted">
                <i className="bi bi-bar-chart-fill me-1"></i> Industry benchmark
                for <strong>{industry}</strong>
              </span>
            </div>
            <div
              className="position-relative w-100"
              style={{
                height: "10px",
                background: "#e0e0e0",
                borderRadius: "50px",
              }}
            >
              <div
                style={{
                  width: `${Math.min((cpc / benchmark) * 100, 100)}%`,
                  backgroundColor: "#6cb2eb",
                  height: "100%",
                  borderRadius: "50px",
                }}
              />
            </div>
            <div className="d-flex justify-content-between mt-1 small text-muted">
              <span>${(benchmark * 0.75).toFixed(2)}</span>
              <span>${(benchmark * 1.2).toFixed(2)}</span>
            </div>
          </div>

          <h5 className="fw-bold">Strategic Recommendations</h5>
          <ListGroup variant="flush">
            {recoData.map((rec, i) => (
              <ListGroup.Item key={i}>
                <span className="badge bg-primary me-2">{i + 1}</span>
                {rec}
              </ListGroup.Item>
            ))}
          </ListGroup>
        </Card>
      )}
    </Container>
  );
};

export default CpcCalculator;
